import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import {
  Controller,
  ControllerRenderProps,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  AssessmentButtons,
  AssessmentCard,
  ControlledTextField,
} from '../shared';
import { selectPresentMeta } from '../../../redux/meta';
import {
  selectReasonVisitAnswers,
  updateAssessment,
} from '../../../redux/assessment';
import { useAppDispatch } from '../../../pages/assessment';
import {
  DispatchUpdateAssessmentInput,
  QuestionTypes,
  Screens,
} from '../../../redux/types';
import {
  EXCLUSIVE_OPTION,
  V2POINT1_EXCLUSIVE_OPTION_FOR_SECONDARY,
} from '../../../helpers/const';
import { rules } from '../demographics/rules';
import { useLDFlags } from '../../providers/ld-flags';
import {
  ReasonVisitAnswer,
  SpecialtyCareTypes,
  V2Point1SpecialtyCareTypes,
} from '../../../clinical/types';
import { selectIsGuardian } from '../../../redux/session';
import { trackConversion } from '../../../experimentation/client';
import { KameleeonGoal } from '../../../experimentation/base';

type FormValues = {
  checkedItems: string[];
  customReasonVisit?: string;
};

const SECONDARY_TEXT_OVERRIDE_LIST: Partial<Record<Screens, string>> = {
  [Screens.RV_KillingYourself]:
    'If you or someone you know is in crisis, call 911 or the Suicide & Crisis Lifeline at 988 right away.',
  [Screens.RV_UseOfDrugs]:
    "This includes medical or recreational cannabis, cocaine, heroin, psychedelics and any prescription medication you're taking without instructions from a doctor.",
};

const OPTIONS_OVERRIDE: Partial<Record<Screens, ReasonVisitAnswer[]>> = {
  [Screens.RV_SecondaryReason]: [
    'Eating disorder',
    'Personality disorder',
    'Schizophrenia or schizoaffective disorder',
    'Self-harm or self-injury behavior',
    'Substance use disorder',
    'No, none of these',
  ],
  [Screens.RV_UseOfDrugs]: [
    'Never',
    'Less than once a month',
    'Monthly',
    'Weekly',
    'Several times per week',
    'Every day or almost every day',
  ],
  [Screens.RV_DrinkingAlcohol]: [
    'Never',
    'Less than once a month',
    'Monthly',
    'Weekly',
    'Several times per week',
    'Every day or almost every day',
  ],
};

const Question = () => {
  const dispatch = useAppDispatch();
  const meta = useSelector(selectPresentMeta);
  const reasonVisitAnswers = useSelector(selectReasonVisitAnswers);
  const isGuardian = useSelector(selectIsGuardian);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { addToast } = useToasts();
  const { shouldUseIntakeV2Point1 } = useLDFlags();
  const MAIN_REASON_UNCERTAIN = "I'm not sure";

  if (isGuardian) {
    OPTIONS_OVERRIDE[Screens.RV_KillingYourself] = [
      'Yes',
      'No',
      "I don't know",
    ];

    if (shouldUseIntakeV2Point1) {
      OPTIONS_OVERRIDE[Screens.RV_SpecialtyCare] = [
        V2Point1SpecialtyCareTypes.LGBTQIA,
        V2Point1SpecialtyCareTypes.Perinatal,
        V2Point1SpecialtyCareTypes.None,
      ];
    }
  }

  const secondaryText =
    meta.secondaryText &&
    shouldUseIntakeV2Point1 &&
    SECONDARY_TEXT_OVERRIDE_LIST[meta.screen] !== undefined
      ? SECONDARY_TEXT_OVERRIDE_LIST[meta.screen]
      : meta.secondaryText;

  const options =
    meta.options &&
    shouldUseIntakeV2Point1 &&
    OPTIONS_OVERRIDE[meta.screen] !== undefined
      ? OPTIONS_OVERRIDE[meta.screen]
      : meta.options;

  useEffect(() => {
    const result = reasonVisitAnswers[meta.screen] || [];
    setIsReadOnly(result.length > 0 ? !meta.canRewrite : false);
  }, [reasonVisitAnswers, meta.screen, meta.canRewrite]);

  useEffect(() => {
    if (meta.screen === Screens.RV_MainReason) {
      void trackConversion(KameleeonGoal.MainReasonForVisitScreenReached);
    }
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      checkedItems: reasonVisitAnswers[meta.screen] || [],
      customReasonVisit: '',
    },
  });

  const watchCheckedItems = watch('checkedItems');
  const showFreeText =
    Array.isArray(watchCheckedItems) &&
    watchCheckedItems.includes(MAIN_REASON_UNCERTAIN);

  const onSubmit: SubmitHandler<FormValues> = async data => {
    if (data.checkedItems.length > 0) {
      const answer: DispatchUpdateAssessmentInput = [
        { questionCode: meta.screen, answerValue: data.checkedItems },
      ];
      if (
        data.checkedItems.includes(MAIN_REASON_UNCERTAIN) &&
        data.customReasonVisit
      ) {
        const freeTextAnswer = {
          questionCode: `${meta.screen}FreeText`,
          answerValue: [data.customReasonVisit],
        };
        answer.push(freeTextAnswer);
      }

      try {
        await dispatch(updateAssessment(answer));
        reset();
      } catch {
        addToast(
          'Sorry, something went wrong. Please refresh the page and try again.'
        );
      }
    }
  };

  useEffect(() => {
    reset({ checkedItems: reasonVisitAnswers[meta.screen] || [] });
  }, [meta, isSubmitSuccessful]);

  const handleCheckboxChange = (
    field: ControllerRenderProps<FormValues, 'checkedItems'>,
    option: string
  ) => {
    if (!field.value.includes(option)) {
      field.onChange([...field.value, option]);
      return;
    }
    const newItems = field.value.filter(topic => topic !== option);
    field.onChange(newItems);
  };

  const enforceExclusiveOption = (option: string) => {
    if (!watchCheckedItems.length) return false;

    if (meta.screen === Screens.RV_SecondaryReason) {
      if (shouldUseIntakeV2Point1) {
        if (
          watchCheckedItems.includes(V2POINT1_EXCLUSIVE_OPTION_FOR_SECONDARY)
        ) {
          return option !== V2POINT1_EXCLUSIVE_OPTION_FOR_SECONDARY;
        }
        return V2POINT1_EXCLUSIVE_OPTION_FOR_SECONDARY === option;
      }

      if (watchCheckedItems.includes(EXCLUSIVE_OPTION))
        return option !== EXCLUSIVE_OPTION;
      return EXCLUSIVE_OPTION === option;
    }
    if (meta.screen === Screens.RV_SpecialtyCare) {
      if (watchCheckedItems.includes(SpecialtyCareTypes.None))
        return option !== SpecialtyCareTypes.None;
      return SpecialtyCareTypes.None === option;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AssessmentCard>
        {meta.type === QuestionTypes.One && (
          <Controller
            name="checkedItems"
            control={control}
            defaultValue={[]}
            rules={rules.requiredCheckboxOrRadio as any}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <RadioGroup {...field}>
                  {options?.map((option, index) => (
                    <FormControlLabel
                      control={<Radio />}
                      disabled={isReadOnly}
                      key={`assessment-radio-key${index}`}
                      label={option}
                      value={option}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
        )}
        {meta.type === QuestionTypes.Many && (
          <Controller
            name="checkedItems"
            control={control}
            rules={rules.requiredCheckboxOrRadio as any}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <FormLabel sx={{ mb: 1 }}>Choose all that apply.</FormLabel>
                {options?.map(
                  (option, index) =>
                    option !== SpecialtyCareTypes.Surgical && (
                      <FormControlLabel
                        control={<Checkbox {...field[index]} />}
                        checked={field.value.includes(option)}
                        key={option}
                        label={option}
                        disabled={isReadOnly || enforceExclusiveOption(option)}
                        onChange={() => handleCheckboxChange(field, option)}
                        // Align checkboxes to top for multiline labels
                        slotProps={{
                          typography: {
                            // Non-standard sizing matches MUI checkbox padding
                            sx: { mt: '9px' },
                          },
                        }}
                        sx={{
                          alignItems: 'flex-start',
                        }}
                      />
                    )
                )}
                {fieldState.error && (
                  <FormHelperText sx={{ ml: 0 }}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        )}
        {meta.screen === 'RV_MainReason' && (
          <Collapse in={showFreeText} unmountOnExit>
            <ControlledTextField<FormValues>
              name="customReasonVisit"
              control={control}
              defaultValue=""
              fullWidth
              autoComplete="off"
              label="Tell us more (optional)"
              inputProps={{ maxLength: 280 }}
              sx={{ mt: 2 }}
              showCharCount
            />
          </Collapse>
        )}
        {secondaryText &&
          secondaryText.split('\n').map(text => (
            <Typography key={text} variant="body2" sx={{ mt: 2 }}>
              {text}
            </Typography>
          ))}
      </AssessmentCard>
      <AssessmentButtons
        primary={{
          children: meta.review ? 'Save' : 'Continue',
          type: 'submit',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default Question;
